const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    this.forms = this.getProductForms();
    this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    //this.updatePrice();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute('data-section-id');
  }

  // Renamed the getter
  getProductForms() {
    return document.getElementsByClassName('shopify-product-form');
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.forms[1]?.querySelector(`input[name="id"]`) ||
      this.forms[1]?.querySelector(`select[name="id"]`) ||
      this.forms[0]?.querySelector(`input[name="id"]`) ||
      this.forms[0]?.querySelector(`select[name="id"]`)
    );
  }

  get priceElement() {
    return this.shopifySection.querySelector('.price');
  }

  get comparedAtPrice() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput.value}"]`,
    );
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption.getAttributeNames().includes('disabled');
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector('.selected-selling-plan-id');
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(hiddenClass);
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add(hiddenClass);
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`,
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`,
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    this.updateSellingPlanInputsValues();
    this.listenToSellingPlanFormRadioButtonChange();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput.tagName === 'INPUT') {
      // Listen for changes in the input field's value
      this.variantIdInput.addEventListener('input', (event) => {
        this.handleVariantChange(event.target.value); // Handle variant change here
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  get regularPriceElement() {
    return this.shopifySection.querySelector('.price__regular');
  }

  get salePriceElement() {
    return this.shopifySection.querySelector('.price__sale');
  }

  get salePriceValue() {
    return this.salePriceElement.querySelector('.price-item--sale');
  }

  get regularPriceValue() {
    return this.salePriceElement.querySelector('.price-item--regular');
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`,
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption.dataset.variantCompareAtPrice;
  }

  get price() {
    return this.sellingPlanAllocationPrices.price ?? null;
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */
  updatePrice() {
    if (
      !this.selectedPurchaseOptionComparedAtPrice ||
      this.selectedPurchaseOptionComparedAtPrice ===
        this.selectedPurchaseOptionPrice
    ) {
      this.showRegularPrice();
      this.hideSalePrice();
      this.priceElement.classList.remove('price--on-sale');
    } else {
      this.showSalePrice();
      this.hideRegularPrice();
      this.priceElement.classList.add('price--on-sale');
    }
  }

  hideSalePrice() {
    this.salePriceElement.style.display = 'none';
  }

  hideRegularPrice() {
    this.regularPriceElement.style.display = 'none';
  }

  showRegularPrice() {
    this.regularPriceElement.style.display = 'block';
    this.shopifySection.querySelector('.price__sale').style.display = 'none';
  }

  showSalePrice() {
    this.salePriceElement.style.display = 'block';
    this.regularPriceValue.innerHTML =
      this.selectedPurchaseOptionComparedAtPrice;
    this.salePriceValue.innerHTML = this.selectedPurchaseOptionPrice;
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll('.selected-selling-plan-id');
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"]:not([name="subscription_main_radio"]):checked',
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  setTextContent(element, textContent) {
    if (element) {
      element.textContent = textContent;
    }
  }

  changeSubscriptionPrice(selectedPurchaseOption) {
    const subscriptionInner = document.querySelector('.subscription_inner');
    const comparePrice = subscriptionInner.querySelector('.subscription_prices del');
    const regularPrice = subscriptionInner.querySelector('.subscription_prices span');
    const perUnitMsg = subscriptionInner.querySelector('.subscription_info .per_unit_price');
    const packText = subscriptionInner.querySelector('.subscription_info .pack_text');
    const selectedSubscriptionText = document.querySelector('.selling_plan_theme_integration  .selected_allocation');

    this.setTextContent(selectedSubscriptionText, `Delivered ${selectedPurchaseOption?.dataset.sellingPlanName?.toLowerCase()}`);
    this.setTextContent(comparePrice, selectedPurchaseOption?.dataset.variantCompareAtPrice);
    this.setTextContent(regularPrice, selectedPurchaseOption?.dataset.variantPrice);
    this.setTextContent(perUnitMsg, selectedPurchaseOption?.dataset.unitMsg);
    this.setTextContent(packText, selectedPurchaseOption?.dataset.packSize);
  }


  updateStickyCTA(e) {
    const sellingPlanElement = document.querySelector("[name='selling_plan']")
    const stickyCTALabel = document.querySelector('.selling_plan_title')
    if (!sellingPlanElement) return
    const sellingPlan = sellingPlanElement.value
    if (sellingPlan) {
      const sellingPlanRadioElement = document.querySelector(`input[data-selling-plan-id='${sellingPlan}']`)
      if (!sellingPlanRadioElement) return
      const sellingPlanName = sellingPlanRadioElement.dataset.sellingPlanName
      stickyCTALabel.textContent = sellingPlanName
    } else {
      stickyCTALabel.textContent = "one-time purchase"
    }
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();

    // this.updatePrice();
      if (selectedPurchaseOption.dataset.radioType == "one_time_purchase") {
        let subscriptionRadio = document.querySelector('input[name="subscription_main_radio"]');
        if (subscriptionRadio.checked) {
          subscriptionRadio.checked = false;
        }
      } else {
        this.changeSubscriptionPrice(selectedPurchaseOption);
      }
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.visibleSellingPlanForm
    ?.querySelectorAll('input[type="radio"]:not([name="subscription_main_radio"])')
    .forEach((radio) => {
      radio.addEventListener('change', (event) => {
        this.handleRadioButtonChange(event.target);
        this.updateStickyCTA(event)
      });
    });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== 'fetch') return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes('variant') || url.search.includes('variants')) {
          //this.updatePrice();
        }
      });
    });

    performanceObserver.observe({entryTypes: ['resource']});
  }
}


// dropdown script
function intializeDropdownScript() {
    document
      .querySelectorAll('.selling_plan_app_container')
      .forEach((sellingPlansWidgetContainer) => {
        new SellingPlansWidget(sellingPlansWidgetContainer);
      });
    const mainParent = document.querySelector('.selling_plan_app_container');
    if(mainParent == null) return; // if we're on an OOS quantity, etc
    var selectedAllocation = document.querySelector('.selected_allocation');
    var dropdownMenu = document.querySelector('.dropdown-menu');
    let onetimeSubscriptionRadio = mainParent.querySelector('[data-radio-type="one_time_purchase"]');
    let mainRadios = document.querySelectorAll('.selling-plan-wrapper-bd');

    mainRadios.forEach(function(mainRadio) {
        mainRadio.addEventListener('click', function() {
            // Get all subscription radio buttons
            let subscriptionRadios = document.querySelectorAll('input.subscription_main_radio');

            subscriptionRadios.forEach(function(subscriptionRadio) {
                subscriptionRadio.checked = false;
                subscriptionRadio.classList.remove('active');
                // Dispatch a change event to notify listeners
                subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
            });

            // Check the radio button related to the clicked mainRadio and add 'active' class
            let targetRadio = this.querySelector('input.subscription_main_radio');
            if (targetRadio) {
                targetRadio.checked = true;
                targetRadio.classList.add('active');
                // Dispatch a change event to notify listeners
                targetRadio.dispatchEvent(new Event('change', { bubbles: true }));

            }
        });
    });

    // Toggle dropdown visibility on click
    selectedAllocation.addEventListener('click', function(e) {
        e.stopPropagation();
        dropdownMenu.style.display = dropdownMenu.style.display === 'block' ? 'none' : 'block';
        let subscriptionRadio = document.querySelector('input[name="subscription_main_radio"]');
        if (!subscriptionRadio) return
        if(!subscriptionRadio.checked) {
            subscriptionRadio.checked = true;
            var event = new Event('change', { bubbles: true });
            subscriptionRadio.dispatchEvent(event);
            onetimeSubscriptionRadio.checked = false;
            var event = new Event('change', { bubbles: false });
            onetimeSubscriptionRadio.dispatchEvent(event);
        }
    });
    
    // Update the selected text and close the dropdown
    var dropdownItems = dropdownMenu.querySelectorAll('li > label');
    dropdownItems.forEach(function(item) {
        item.addEventListener('click', function() {
            // Update the selected text
            Array.prototype.map.call(dropdownItems, label => label.classList.remove('active'));
            item.classList.add('active');
          if(item.closest('ul').classList.contains('onetime_menu')) {
            onetimeSubscriptionRadio.checked = true;
            var event = new Event('change', { bubbles: true });
            onetimeSubscriptionRadio.dispatchEvent(event);
          }

          dropdownMenu.style.display = 'none';
        });
    });
  
    // Close the dropdown if clicking outside
    document.addEventListener('click', function(event) {
      if (!selectedAllocation) return
        if (!selectedAllocation.contains(event.target) && !dropdownMenu.contains(event.target)) {
            dropdownMenu.style.display = 'none';
        }
    });

    //sticky cart JS
    const productDescription = document.querySelector(
      ".product__info .sticky-cart-observe"
    );
    const stickyProductDetails = document.querySelector(
      ".product-sticky_main"
    );

    if (productDescription && stickyProductDetails) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              stickyProductDetails.classList.add("visible");
            } else {
              stickyProductDetails.classList.remove("visible");
            }
          });
        },
        { threshold: [0] }
      );

      observer.observe(productDescription);
    }

    const getSubscriptionWrapper = document.querySelector(".selling-plan-wrapper-bd-subscribe");

    getSubscriptionWrapper.addEventListener("click", ()=>{
        const getSubscriptionMenu = document.querySelector(".subscription_menu-items.active");
        getSubscriptionMenu.click();
    })
}

document.addEventListener('DOMContentLoaded', intializeDropdownScript);
document.addEventListener("barba-transitioned", (...args) => {
  console.log('barba-transitioned', args);
  intializeDropdownScript()
})


